import { memo } from "react";
import { CALCULATION_METHODS, FACTOR_GASES } from "../KPIGrid/model/options";
import { KG_CO2E } from "../../../_constants/emissionUnits";

const METHOD_LABELS = Object.fromEntries(CALCULATION_METHODS.map(({ value, shortLabel }) => [value, shortLabel]));

/**
 * @param {number|null} value
 * @returns {string}
 */
const formatNumber = (value) =>
  value == null ? "—" : Number(value).toLocaleString("en-IN", { maximumSignificantDigits: 6 });

/**
 * Each gas's share of a factor's kg CO2e, as one bar split three ways.
 *
 * @param {object} props
 * @param {import("./emissionFactorBreakdownApi").BreakdownFactor["co2e_per_unit_by_gas"]} props.contributions
 * @param {number|null} props.total
 */
function GasShareBar({ contributions, total }) {
  if (!(total > 0)) return null;
  return (
    <div className="efb-share" role="img" aria-label={`Share of ${KG_CO2E} by gas`}>
      {FACTOR_GASES.map(({ gas, label }) => {
        const share = (contributions[gas] ?? 0) / total;
        if (!(share > 0)) return null;
        return (
          <span
            key={gas}
            className={`efb-share-part efb-share-part--${gas}`}
            style={{ width: `${Math.max(share * 100, 1.5)}%` }}
            title={`${label}: ${(share * 100).toFixed(2)}%`}
          />
        );
      })}
    </div>
  );
}

/**
 * One gas cell: the factor as stored, the GWP it is weighted by (none when
 * the gas values are already CO2e), and the kg per unit it adds; only the
 * total column is labelled kg CO2e.
 *
 * @param {object} props
 * @param {import("./emissionFactorBreakdownApi").BreakdownFactor} props.factor
 * @param {"co2"|"ch4"|"n2o"} props.gas
 */
function GasCell({ factor, gas }) {
  const ef = factor[`ef_${gas}`];
  if (ef == null) return <td className="efb-gas efb-muted">—</td>;
  const gwp = factor[`gwp_${gas}`];
  return (
    <td className="efb-gas">
      <span className="efb-gas-ef">{formatNumber(ef)}</span>
      {factor.ef_basis === "CO2E" || gwp == null ? null : <span className="efb-gas-gwp">× {formatNumber(gwp)}</span>}
      <span className="efb-gas-result">{formatNumber(factor.co2e_per_unit_by_gas[gas])}</span>
    </td>
  );
}

/**
 * A KPI and every factor that prices it, in the order the company side
 * applies them (database priority first).
 *
 * @param {object} props
 * @param {import("./emissionFactorBreakdownApi").BreakdownKpi} props.kpi
 * @param {string} props.moduleName
 * @param {boolean} props.open
 * @param {(kpiId: number) => void} props.onToggle
 */
function KpiFactorCard({ kpi, moduleName, open, onToggle }) {
  const gasWiseCount = kpi.factors.filter((factor) => factor.has_gas_breakdown).length;

  return (
    <section className={`efb-kpi${open ? " efb-kpi--open" : ""}`}>
      <button type="button" className="efb-kpi-head" aria-expanded={open} onClick={() => onToggle(kpi.kpi_id)}>
        <span className="efb-kpi-caret" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="efb-kpi-title">{kpi.kpi_title}</span>
        <span className="efb-kpi-meta">
          {moduleName ? <span>{moduleName}</span> : null}
          <span>KPI {kpi.kpi_id}</span>
          <span>
            {kpi.factors.length} factor{kpi.factors.length === 1 ? "" : "s"}
          </span>
          {gasWiseCount ? <span className="efb-badge">{gasWiseCount} gas-wise</span> : null}
        </span>
      </button>

      {open ? (
        <div className="efb-table-wrap">
          <table className="efb-table">
            <thead>
              <tr>
                <th scope="col">Scope · Country</th>
                <th scope="col">Per unit</th>
                <th scope="col">Source</th>
                <th scope="col">Method</th>
                <th scope="col">Density · NCV</th>
                {FACTOR_GASES.map(({ gas, label }) => (
                  <th scope="col" key={gas} className="efb-gas-head">
                    {label}
                    <small>factor · GWP · kg</small>
                  </th>
                ))}
                <th scope="col" className="efb-num">
                  <span style={{ textTransform: "none" }}>{KG_CO2E} / unit</span>
                </th>
                <th scope="col">GWP source</th>
              </tr>
            </thead>
            <tbody>
              {kpi.factors.map((factor) => (
                <tr key={factor.id}>
                  <td>
                    <span className="efb-scope" data-scope={String(factor.scope).replace(/\D/g, "")}>
                      {factor.scope}
                    </span>
                    <span className="efb-muted"> · {factor.country_code}</span>
                  </td>
                  <td>{factor.unit}</td>
                  <td>{factor.ghg_database_name || "—"}</td>
                  <td>{factor.has_gas_breakdown ? METHOD_LABELS[factor.calculation_method] : "Single value"}</td>
                  <td className="efb-num">
                    {factor.calculation_method === "FUEL_ENERGY" ? (
                      <>
                        {factor.density == null ? null : `${formatNumber(factor.density)} kg · `}
                        <span className="efb-muted">{formatNumber(factor.ncv)} TJ/Gg</span>
                        <div className="efb-muted">= {formatNumber(factor.energy_tj_per_unit)} TJ / {factor.unit}</div>
                      </>
                    ) : (
                      <span className="efb-muted">—</span>
                    )}
                  </td>
                  {FACTOR_GASES.map(({ gas }) => (
                    <GasCell key={gas} factor={factor} gas={gas} />
                  ))}
                  <td className="efb-num efb-total">
                    {formatNumber(factor.emission_factor)}
                    <GasShareBar contributions={factor.co2e_per_unit_by_gas} total={factor.emission_factor} />
                  </td>
                  <td>{factor.gwp_source || <span className="efb-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

export default memo(KpiFactorCard);
